const SCROLL_ANIMATION_TRIGGER_CLASSNAME = "scroll-trigger";
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = "scroll-trigger--offscreen";
const SCROLL_ANIMATION_CANCEL_CLASSNAME = "scroll-trigger--cancel";

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (
        elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)
      ) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute("data-cascade"))
          elementTarget.setAttribute("style", `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

/**
 * Safe helper: returns a root that supports querySelectorAll.
 */
function getQueryRoot(rootEl) {
  if (rootEl && typeof rootEl.querySelectorAll === "function") return rootEl;
  return document;
}

function initializeCinematicImageReveal(rootEl = document, isDesignModeEvent = false) {
  const root = getQueryRoot(rootEl);

  const reduceMotion =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const isInsideParallax = (el) =>
    !!el.closest("parallax-background-banner, .parallax-background-banner, .section-parallax-background-banner");

  const wrapperTargets = Array.from(root.querySelectorAll(".wt-cinematic-reveal")).filter(
    (el) => !isInsideParallax(el),
  );

  // Allow putting the class directly on the media element:
  // <img class="wt-cinematic-reveal"> ... → we'll animate the nearest wrapper (picture/parent).
  const mediaTargets = Array.from(root.querySelectorAll("img.wt-cinematic-reveal, svg.wt-cinematic-reveal")).filter(
    (el) => !isInsideParallax(el),
  );

  const inferredWrappers = mediaTargets
    .map((media) => media.closest("picture") || media.parentElement)
    .filter(Boolean)
    .map((wrapper) => {
      // Ensure mask animation applies to a container (not only to <img/> fade/scale).
      // This is the key for the "cinematic unmask" effect.
      wrapper.classList.add("wt-cinematic-reveal");
      return wrapper;
    });

  const targets = Array.from(new Set([...wrapperTargets, ...inferredWrappers])).filter(
    (el) => el && !isInsideParallax(el),
  );

  if (!targets.length) return;

  const reveal = (el) => {
    if (!el) return;
    if (el.classList.contains("is-revealed")) return;

    // Ensure the initial masked state has been painted before toggling
    // "is-revealed" (prevents some browsers from skipping the transition).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.add("is-revealed");
      });
    });
  };

  const conceal = (el) => {
    if (!el) return;
    el.classList.remove("is-revealed");
  };

  if (reduceMotion || isDesignModeEvent || !("IntersectionObserver" in window)) {
    targets.forEach((el) => reveal(el));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        if (entry.isIntersecting) reveal(el);
        else conceal(el);
      });
    },
    {
      threshold: 0.25,
      rootMargin: "0px 0px -10% 0px",
    },
  );

  targets.forEach((el) => {
    io.observe(el);
  });
}

function initializeScrollAnimationTrigger(
  rootEl = document,
  isDesignModeEvent = false,
) {
  const root = getQueryRoot(rootEl);

  const animationTriggerElements = Array.from(
    root.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME),
  );

  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add("scroll-trigger--design-mode");
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: "0px 0px -50px 0px",
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

window.addEventListener("DOMContentLoaded", () => {
  initializeScrollAnimationTrigger();
  initializeCinematicImageReveal();
});

if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) =>
    (initializeScrollAnimationTrigger(event.target, true),
    initializeCinematicImageReveal(event.target, true)),
  );
  document.addEventListener("shopify:section:reorder", () =>
    (initializeScrollAnimationTrigger(document, true),
    initializeCinematicImageReveal(document, true)),
  );
}
